import { useEffect, useRef, useState } from "react";
import { embedDashboard } from "@superset-ui/embedded-sdk";
import { Maximize2 } from "lucide-react";
import {
  fetchSupersetGuestToken,
  type SupersetFilters,
} from "../../utils/portfolio-risk-concentration/api/superset-views";

interface SupersetEmbedProps {
  /** Frame header label */
  title: string;
  /** Embedded-registration uuid from GET /superset/dashboard */
  uuid?: string | null;
  /** Origin Superset is served from */
  supersetDomain?: string;
  /** Frame body height. A number is px; a string is passed to CSS as-is, which is how the
   *  expanded view fills the viewport. */
  height?: number | string;
  /**
   * Where the "Expand" button goes — an ATHENA route, never a Superset URL.
   *
   * That distinction is the whole feature. Superset is fully login-gated and this embed
   * works only because prc-api mints a short-lived guest token that the SDK hands to the
   * iframe; a link to `supersetDomain` carries no such token, so it lands on Superset's
   * sign-in page, and even past it would show the dashboard UNFILTERED — the page's
   * selections live in the token's RLS, not in the URL. So expanding opens another page of
   * this app, which repeats the same guest-token mount at full size with the same filters.
   *
   * Omitted (the default) renders no button.
   */
  expandHref?: string;
  /** Still resolving the embed details */
  loading?: boolean;
  /** Which dashboard in the backend's registry — passed through to the guest-token
   *  request so the token is scoped to the dashboard actually being mounted. */
  dashboardKey?: string;
  /**
   * What the page's filter bar narrows this dashboard to. Sent to prc-api, which turns it
   * into the guest token's RLS clauses — so the filtering happens in Superset's own query
   * layer and there is no filter bar inside the frame at all.
   *
   * Changing it remounts the frame: RLS lives in the token, and the SDK has no API to swap
   * the token or push a filter into a mounted dashboard.
   *
   * This replaced a `urlParams` bag that passed `{scope: "External"}` into the iframe URL.
   * Superset never bound that to its native `scope` filter — nothing in this deployment
   * could have — so the page's selector was decorative and the dashboard showed whatever
   * the viewer's own filter bar was left on.
   */
  filters?: SupersetFilters;
}

/**
 * Width of Superset's collapsed native filter bar — the narrow strip down the left of the
 * dashboard carrying an expand arrow and a funnel icon.
 *
 * We shift the iframe left by this much and clip it, because **there is no supported way to
 * turn that strip off in Superset 4.1.1.** Verified against the running build's own bundles
 * rather than assumed:
 *
 *   - `show_filters` is declared in Superset's URL_PARAMS but read NOWHERE — it occurs exactly
 *     once across every asset bundle, at its own definition. So the SDK's
 *     `dashboardUiConfig.filters.visible: false` (which becomes `?show_filters=false`) is a
 *     dead parameter. It is left set below to record the intent, not because it does anything.
 *   - `expand_filters` IS read, which is why the bar arrives collapsed rather than open. That
 *     is as far as the SDK can get.
 *   - `filter_bar_orientation: "HORIZONTAL"` would move it out of the gutter, but the bundle
 *     gates it on the `HORIZONTAL_FILTER_BAR` feature flag, which this deployment does not
 *     enable (`FEATURE_FLAGS` in superset_config_docker.py is assigned, not merged).
 *   - `standalone` only suppresses the top nav in this build, not the filter bar.
 *
 * The iframe is cross-origin, so its DOM cannot be styled from here — geometry is the only
 * lever we have. The dashboard has no native filters at all (filters reach it as guest-token
 * RLS instead), so nothing interactive is being hidden.
 *
 * If a future Superset stops rendering the strip, this offset would start clipping real
 * content by 32px — so it is a single named constant, and this is the only place to change.
 */
const SUPERSET_FILTER_RAIL_PX = 32;

/** Embeds a Superset dashboard using the official embedded SDK.
 *
 *  The SDK mounts an iframe and authenticates it with a *guest token* minted per user by
 *  prc-api, rather than relying on a Superset session. That is what lets an Athena user who
 *  has never logged into Superset see the dashboard, while Superset itself stays fully
 *  login-gated — the token grants one dashboard and one role, and expires in minutes. The
 *  SDK calls fetchGuestToken again on its own whenever the token nears expiry.
 *
 *  A plain <iframe src=...> cannot do this: it would carry no credentials, and Superset
 *  would serve its login page inside the frame.
 */
export function SupersetEmbed({
  title,
  uuid,
  supersetDomain,
  height = 720,
  expandHref,
  loading = false,
  dashboardKey,
  filters,
}: SupersetEmbedProps) {
  const mount = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  // A string height means "fill whatever the parent gives you" (the expanded tab passes
  // "100%"); a number is the fixed-px panel. The two need different flex behaviour — see the
  // body div below — so the distinction is named once here rather than inferred twice.
  const fills = typeof height === "string";

  // The effect below remounts the dashboard whenever the filters change, so it needs a
  // value that is stable across renders when the CONTENT has not changed. An object
  // literal from the caller is a new reference every render and would remount the
  // iframe on each one.
  const filtersKey = JSON.stringify(filters ?? {});

  useEffect(() => {
    if (!uuid || !supersetDomain || !mount.current) return;

    let cancelled = false;
    let unmount: (() => void) | undefined;
    setError(null);
    setReady(false);

    embedDashboard({
      id: uuid,
      supersetDomain,
      mountPoint: mount.current,
      // Reads filtersKey, not `filters`, so the token the SDK re-mints on expiry carries the
      // same RLS as the mount it belongs to.
      fetchGuestToken: () => fetchSupersetGuestToken(dashboardKey, JSON.parse(filtersKey)),
      iframeTitle: title,
      dashboardUiConfig: {
        hideTitle: true,
        hideTab: true,
        // MUST stay false: this is what keeps the per-chart "..." menu, and therefore the
        // Download > Export to .CSV / Export to Excel items, reachable inside the frame.
        // Setting it true would remove the only download affordance the embed has.
        hideChartControls: false,
        // No filter bar inside the frame. The page owns the filters and applies them as
        // guest-token RLS; a second set of controls in here could only contradict them,
        // and used to, because the page's own selector did not actually filter anything.
        //
        // `expanded: false` works. `visible: false` does NOT — see SUPERSET_FILTER_RAIL_PX
        // above; it is a dead param in 4.1.1 and the collapsed strip is clipped instead.
        filters: { expanded: false, visible: false },
      },
    })
      .then((embedded) => {
        if (cancelled) {
          embedded.unmount();
          return;
        }
        unmount = embedded.unmount;
        // The SDK appends a bare iframe with no dimensions of its own; without this the
        // frame collapses to a few pixels regardless of the container's height.
        const frame = mount.current?.querySelector("iframe");
        if (frame) {
          // Overwide and pulled left so Superset's collapsed filter strip lands outside the
          // clipping wrapper. The extra width keeps the dashboard's usable area the same as
          // before, rather than trading the strip for 32px of lost content.
          frame.style.width = `calc(100% + ${SUPERSET_FILTER_RAIL_PX}px)`;
          frame.style.marginLeft = `-${SUPERSET_FILTER_RAIL_PX}px`;
          frame.style.height = "100%";
          frame.style.border = "0";
        }
        setReady(true);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });

    return () => {
      cancelled = true;
      try {
        unmount?.();
      } catch {
        /* the iframe may already be gone with the unmounted node */
      }
    };
  }, [uuid, supersetDomain, dashboardKey, filtersKey, title]);

  const message = loading
    ? "Loading dashboard…"
    : error
      ? error
      : !uuid
        ? "This dashboard is not registered for embedding in Superset."
        : !ready
          ? "Connecting to Superset…"
          : null;

  return (
    <section
      className={`flex flex-col overflow-hidden rounded-lg border border-border bg-card${
        fills ? " h-full" : ""
      }`}
    >
      {/* Still no "open in Superset" affordance, and there must never be one: a raw Superset
          tab honours none of the page's filters — they live in the guest token's RLS, not in
          the URL — so it would show the unfiltered dashboard, behind a login the viewer has
          no account for. Expand goes to an Athena route that re-mounts this same embed at
          full size. See expandHref. */}
      <header className="flex shrink-0 items-center justify-between gap-4 border-b border-border px-5 py-3">
        <h3 className="text-sm font-semibold text-card-foreground">{title}</h3>
      </header>

      {/* overflow-hidden is load-bearing, not tidiness: it is what actually clips the
          filter strip the iframe is offset by.

          `flex-1 min-h-0` ONLY when filling. Applying it to the fixed-height panel too would
          collapse it: `flex-1` is `flex: 1 1 0%`, and a 0% basis beats the 720px height
          inside a flex column whose own height is content-driven. */}
      <div
        className={`relative overflow-hidden bg-background${fills ? " min-h-0 flex-1" : ""}`}
        style={{ height }}
      >
        <div ref={mount} className="h-full w-full" />
        {message && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center text-sm text-muted-foreground">
            <span>{message}</span>

          </div>
        )}

        {/* Same affordance, same place, same styling as the Power BI panel below it on
            Snapshot — bottom-right of the frame, icon only. Two embeds of two different
            products sitting one above the other should not offer "make this bigger" in two
            different shapes; the classes here are deliberately identical to PowerBIEmbed's.

            Rendered only once the dashboard is actually up, mirroring PowerBIEmbed showing
            its button only when there is a `src`: an expand control over "Connecting to
            Superset…" offers to enlarge nothing, and would sit on top of that message. */}
        {expandHref && ready && !message && (
          <a
            href={expandHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open ${title} in a new tab`}
            className="absolute bottom-4 right-4 inline-flex h-9 w-9 items-center justify-center rounded-md bg-accent text-accent-foreground shadow-md transition-colors hover:bg-accent/80"
          >
            <Maximize2 className="h-4 w-4" />
          </a>
        )}
      </div>
    </section>
  );
}
